import { FC, useEffect } from 'react'
import { Link, Outlet, redirect, useNavigate, useLocation } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import * as Sentry from "@sentry/react"
import './sign-up.css'

// COMPONENTS
import * as Icons from '@icons'
import { Column } from '@components/templates/column'

// ROUTES
import BillingRoute from './billing'
import KycRoute from './kyc'
import ReloadableCardRoute from './reloadable-card'
import AddInfoRoute from './add-info'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { createLoader, fetchClerk } from '@utils/loaders'
import { getRequiredOnboardingPath } from '@utils/user-onboarding'

const Layout: FC = () => {
    const { isLoaded, user } = useUser()
    const navigate = useNavigate()
    const location = useLocation()

    useEffect(() => {
        if (isLoaded && user) {
            const targetPath = getRequiredOnboardingPath(user, document.location.search)
            const currentPath = `${location.pathname}${location.search}`
            const isBareSignUpPath = location.pathname === '/sign-up' || location.pathname === '/sign-up/'

            if (targetPath && targetPath !== currentPath) {
                Sentry.addBreadcrumb({ category: 'sign-up', message: 'missing required onboarding data, redirecting', level: 'info', data: { targetPath } })
                navigate(targetPath, { replace: true })
                return
            }

            if (isBareSignUpPath) {
                navigate('/account', { replace: true })
            }
        }
    }, [isLoaded, user, navigate, location])

    return (
        <Column className="kyc-stack">
            <Column.Header.End>
                <Link className="account-redirect-container" to="/account">
                    <Icons.House tint="var(--splitcard-black)" />
                </Link>
            </Column.Header.End>
            <Outlet />
        </Column>
    )
}

const loader = createLoader(async ({ request }) => {
    const Clerk = await fetchClerk()
    if (Clerk.user) return null

    const signUp = Clerk.client?.signUp
    if (signUp?.status === 'missing_requirements') return null

    const params = new URLSearchParams()
    params.set('from', new URL(request.url).pathname)
    return redirect(`/sign-in?${params.toString()}`)
})

const SignUpRoute: RouteObject = {
    path: "sign-up",
    element: <Layout />,
    loader,
    children: [
        AddInfoRoute,
        BillingRoute,
        KycRoute,
        ReloadableCardRoute
    ],
}

export default SignUpRoute
